import { useEffect, useState } from 'react'

/**
 * Full-screen bootstrap loader. With `waking`, the database is resuming from
 * an overnight scale-to-zero (see AuthContext.dbReady) and the wait can run
 * 10–30 s — the bare spinner reads as "frozen", so we show a rotating, lightly
 * cheeky caption to make it feel intentional and on-brand. The caption only
 * appears after a short delay, so a fast (already-warm) login never flashes
 * text and just shows the normal quick spinner.
 */
const WAKE_MESSAGES = [
  'Getting a head start, are we?',
  'Waking up the database…',
  'Brewing your pipeline…',
  'Dusting off the deals…',
  'Rounding up your prospects…',
  'Stretching our legs…',
  'Almost there…',
]

// Delay before the first caption shows — long enough that a warm-DB login
// (resolves in well under a second) never flashes a message.
const CAPTION_DELAY_MS = 1200
const ROTATE_MS = 3500

export function AppLoader({ waking = false }: { waking?: boolean }) {
  const [idx, setIdx] = useState(0)
  const [showCaption, setShowCaption] = useState(false)

  useEffect(() => {
    if (!waking) {
      setShowCaption(false)
      setIdx(0)
      return
    }
    const reveal = setTimeout(() => setShowCaption(true), CAPTION_DELAY_MS)
    const rotate = setInterval(() => setIdx((i) => (i + 1) % WAKE_MESSAGES.length), ROTATE_MS)
    return () => { clearTimeout(reveal); clearInterval(rotate) }
  }, [waking])

  return (
    <div className="grid h-screen supports-[height:100dvh]:h-dvh place-items-center text-slate-500">
      <div className="flex flex-col items-center gap-4">
        <div className="penser-thinking">
          <img src="/penser-icon.png" alt="" width={48} height={48} />
        </div>
        {waking && (
          <div className={`flex flex-col items-center gap-2 transition-opacity duration-500 ${showCaption ? 'opacity-100' : 'opacity-0'}`}>
            <div aria-live="polite" className="min-h-[1.25rem] text-sm text-slate-400">
              {WAKE_MESSAGES[idx]}
            </div>
            {/* Plain-language explanation so the wait reads as intentional, not
                broken — the #1 reason a user would give up + close the tab. */}
            <p className="max-w-xs text-center text-xs leading-relaxed text-slate-400">
              Outside business hours the system rests to save cost and wakes on
              first use. This usually takes about 30&nbsp;seconds — please keep
              this tab open and it&rsquo;ll continue automatically.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
